import { useEffect, useRef } from 'react';
import { MapLocationData, MapMode } from '@hero/web-components';

export interface UseMapNotificationsOptions {
  /** Current editing index */
  editingIndex: number | null;
  /** Current locations */
  locations: MapLocationData[];
  /** Current map mode */
  mode: MapMode;
  /** Callback when locations are ready */
  onLocationsReady?: (locations: MapLocationData[]) => void;
  /** Callback when the pins settle but the user hasn't saved yet */
  onLocationsFinalized?: (locations: MapLocationData[]) => void;
  /** Whether confirm locations button is enabled */
  enableConfirmLocationsButton: boolean;
  /** Function to update confirmed locations */
  updateConfirmedLocations: (locations: MapLocationData[]) => void;
}

/**
 * Hook for notifying parent component when locations are finalized
 * Handles notifications when editing completes or mode changes
 */
export function useMapNotifications({
  editingIndex,
  locations,
  mode,
  onLocationsReady,
  onLocationsFinalized,
  enableConfirmLocationsButton,
  updateConfirmedLocations,
}: UseMapNotificationsOptions) {
  // Notify parent when single location edit completes (editingIndex becomes null)
  // BUT do NOT update confirmed locations - let user explicitly save via "Save Changes" button
  const prevEditingIndexForNotificationRef = useRef<number | null>(null);
  useEffect(() => {
    const editJustEnded =
      prevEditingIndexForNotificationRef.current !== null && editingIndex === null;

    if (editJustEnded && onLocationsReady && !enableConfirmLocationsButton) {
      onLocationsReady([...locations]);
      // Do NOT call updateConfirmedLocations here - keep "Edited" indicator until explicit save
    }

    // Fires regardless of `enableConfirmLocationsButton`: the point is to
    // let the parent do pre-save work (quote a price, check a lane) at the
    // moment the pins settle rather than making the user discover it after
    // clicking Save.
    if (editJustEnded) onLocationsFinalized?.([...locations]);

    prevEditingIndexForNotificationRef.current = editingIndex;
  }, [
    editingIndex,
    locations,
    onLocationsReady,
    onLocationsFinalized,
    enableConfirmLocationsButton,
    updateConfirmedLocations,
  ]);

  // Track when edit mode ends to notify parent
  // BUT do NOT update confirmed locations - let user explicitly save via "Save Changes" button
  const prevModeRef = useRef<MapMode>(mode);
  useEffect(() => {
    if (
      (prevModeRef.current === 'edit' || prevModeRef.current === 'add_new_location') &&
      mode === 'view' &&
      onLocationsReady &&
      !enableConfirmLocationsButton
    ) {
      onLocationsReady([...locations]);
      // Do NOT call updateConfirmedLocations here - keep "Edited" indicator until explicit save
    }
    prevModeRef.current = mode;
  }, [mode, locations, onLocationsReady, enableConfirmLocationsButton, updateConfirmedLocations]);
}
